import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router';
import { Grid } from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { useCurrentUser } from '@klp/auth';
import { BuildingTabs } from '@klp/bygg/classes';
import { ByggRoutes } from '@klp/bygg/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes, isDeploymentSv } from '@klp/core/app/config';
import { ActionLink } from '@klp/design/ActionButton';
import { Block } from '@klp/design/Block';
import { Loader } from '@klp/design/Loader';
import { ErrorNotification } from '@klp/errorNotification';
import { FileObject } from '@klp/file/classes';
import { useFileContext } from '@klp/file/context/FileContext';
import { Main } from '@klp/layout';

import { Saksmelding, VedleggSak } from '../classes';
import type { SaksmeldingInput } from '../classes';
import { SakSingle, SakSingleActions } from '../components';
import { SakRoutes } from '../config';
import { redirectToExternalUrl } from '../helpers/SakHelpers';
import { useGetSakQuery, usePostMeldingMutation } from '../rtkApi';

export const SakView: React.FC = () => {
    const { t } = useTranslation();
    const { clearQueues } = useFileContext();
    const history = useHistory();
    const location = useLocation<{
        referrer?: 'bygg' | 'sak';
        byggNummer?: string;
    }>();
    const { sakId } = useParams<{ sakId: string }>();

    const {
        data: sakData,
        error: sakError,
        isLoading: sakLoading,
    } = useGetSakQuery(sakId!, {
        skip: !sakId,
    });

    const [
        postMelding,
        {
            isLoading: saksmeldingSaving,
            isSuccess: meldingSuccess,
            isError: meldingError,
        },
    ] = usePostMeldingMutation();

    const sak = sakData?.sak;
    const { currentUser: user } = useCurrentUser();

    // Dalux-saker håndteres i Dalux, f.eks. når noen åpner en gammel lenke eller et bokmerke til saken
    useEffect(() => {
        if (sak?.isDalux) {
            redirectToExternalUrl(sak.eksternUrl!);
        }
    }, [sak]);

    // Clear file queues on mount to reset state from previous session
    useEffect(() => {
        clearQueues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmitMelding = async (
        event: React.FormEvent<HTMLFormElement>,
        input: SaksmeldingInput,
    ) => {
        event.preventDefault();

        const { melding, sakId: inputSakId } = input;

        const vedleggSakList = [
            ...(input.sakImages || []),
            ...(input.sakAttachments || []),
        ].map((img: FileObject) =>
            VedleggSak.parseObject(FileObject.toBinaryFile(img)),
        );

        const request = Saksmelding.parseObject({
            melding,
            sakId: inputSakId,
            vedleggSakList,
        });

        try {
            await postMelding(request).unwrap();
        } catch (error) {
            // Error handled by mutation
        }
    };

    const onBackNaviation = () => {
        if (location.state?.referrer === 'bygg') {
            history.push(
                ByggRoutes.single(
                    location.state?.byggNummer,
                    BuildingTabs.tickets,
                ),
            );

            return;
        }

        if (location.state?.referrer === 'sak') {
            history.push(SakRoutes.list());

            return;
        }

        history.push(SakRoutes.list());
    };

    // Handle melding post notifications
    useEffect(() => {
        if (meldingSuccess) {
            // Clear file upload queues after successful message post
            clearQueues();
            showToast({ body: t('general.saved'), type: 'success' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meldingSuccess]);

    useEffect(() => {
        if (meldingError) {
            showToast({ body: t('general.errorMessage'), type: 'error' });
        }
    }, [meldingError]);

    const showActions = () => {
        return (
            user &&
            (user?.isAdmin || (sak && user?.email === sak?.opprettetAv)) &&
            !isDeploymentSv()
        );
    };

    return (
        <Main>
            <Block paddingBottom="tiny">
                <ActionLink
                    actionType="linkBack"
                    translation="sak.backButton"
                    onClick={onBackNaviation}
                />
            </Block>
            <Loader isLoading={sakLoading || !!sak?.isDalux}>
                <Conditional
                    condition={!sakError}
                    errorComponent={
                        <ErrorNotification
                            title="sak.errorDownload.title"
                            description="sak.errorDownload.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                        />
                    }
                >
                    <Conditional
                        condition={!(sak === undefined && !sakLoading)}
                        errorComponent={
                            <ErrorNotification
                                title="sak.errorNotFound.title"
                                backTarget={SakRoutes.list()}
                                backTitle="sak.goBackToSaker"
                            />
                        }
                    >
                        <Grid container={true}>
                            <Grid item={true} xs={12}>
                                {showActions() && (
                                    <SakSingleActions sak={sak} />
                                )}
                            </Grid>
                            <Grid item={true} sm={12} md={10}>
                                {sak && (
                                    <SakSingle
                                        sak={sak}
                                        onSubmitMelding={onSubmitMelding}
                                        disabled={saksmeldingSaving}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Conditional>
                </Conditional>
            </Loader>
        </Main>
    );
};
